import { Modal } from 'antd';
import { getMonitorDescriptionByName } from '../../../asset/util/monitorHelper';
import { ErrorMessage, Formik, useFormikContext } from 'formik';
import { array, boolean, object } from 'yup';
import {
  Divider,
  Flex,
  numberValidation,
  stringValidation,
  useAssetTranslation,
} from '@mss/mss-component-library';
import Button from '../../Button';
import SubmitButton from '../../SubmitButton';
import React from 'react';
import { ConfigJsonSchema, ConfigJsonSchemaProperty } from '../../../index';
import { XCircle } from 'react-feather';
import { Form } from 'formik-antd/lib/form';
import { Input } from 'formik-antd/lib/input';
import { InputNumber } from 'formik-antd/lib/input-number';
import { EnrichedAssetMonitor, MonitorConfig } from './MonitorSettings';

type ModalProps = {
  monitor: null | EnrichedAssetMonitor;
  close: () => void;
  submit: (monitor: EnrichedAssetMonitor) => Promise<void>;
};

const jsonSchemaToYup = (schema: ConfigJsonSchema, key: string) => {
  const required = schema.required.includes(key);
  const propSchema = schema.properties[key];
  const type = propSchema.type;
  const yupMap = {
    string: stringValidation,
    number: numberValidation,
    boolean: ({ required }: validationFactoryProps) =>
      required ? boolean().required() : boolean(),
    object: ({ required }: validationFactoryProps) =>
      required ? object().required() : object(),
  };

  type validationFactoryProps = { required: boolean };

  switch (type) {
    case 'array':
      return object({
        [key]: array().of(
          yupMap[propSchema.items?.type ?? 'string']({ required })
        ),
      });
    default:
      return object({ [key]: yupMap[type]({ required }) });
  }
};

const jsonSchemaInitialValue = (
  type: ConfigJsonSchemaProperty['type'],
  subType?: ConfigJsonSchemaProperty['type']
) => {
  const typeMap = {
    array: [],
    string: '',
    number: 0,
    object: {},
    boolean: false,
  };
  switch (type) {
    case 'array':
      return [typeMap[subType ?? 'string']];
    default:
      return typeMap[type];
  }
};

const MonitorConfigModal = ({ monitor, close, submit }: ModalProps) => {
  const { t } = useAssetTranslation(['monitor', 'applicationsSecurity']);
  return (
    <Modal
      footer={null}
      closable
      maskClosable
      destroyOnClose
      onCancel={close}
      open={!!monitor}
      title={t('((monitorName)) configuration', {
        monitorName: getMonitorDescriptionByName(monitor?.name ?? '', t).name,
      })}
    >
      {monitor?.config_json_schema != null && (
        <div>
          {!monitor.id && (
            <p>
              {t(
                'This monitor needs configuration before it can be enabled.'
              )}
            </p>
          )}
          <Formik
            initialValues={{
              ...monitor,
              config: Object.entries({
                ...(monitor.config_json_schema.properties ?? {}),
              }).reduce(
                (acc, [key, value]) => ({
                  ...acc,
                  [key]:
                    monitor.config?.[key] ??
                    jsonSchemaInitialValue(value.type, value.items?.type),
                }),
                {} as MonitorConfig
              ),
            }}
            validationSchema={object({
              config: Object.keys({
                ...(monitor.config_json_schema.properties ?? {}),
              }).reduce((acc, key) => {
                return acc.concat(
                  jsonSchemaToYup(monitor.config_json_schema!, key)
                );
              }, object()),
            })}
            onSubmit={async values => {
              await submit(values);
            }}
          >
            {({ values, errors, dirty }) => {
              const required = (key: string) =>
                monitor.config_json_schema!.required.includes(key);
              return (
                <div>
                  <Form>
                    {Object.keys(values.config).map(key => {
                      const property =
                        monitor.config_json_schema!.properties![key];
                      if (property.type === 'array') {
                        return (
                          <MultiInput
                            key={key}
                            propertyName={key}
                            required={required(key)}
                            {...property}
                            title={property.title}
                          />
                        );
                      }
                      return (
                        <Form.Item
                          key={key}
                          name={`config.${key}`}
                          label={t(`${property.title}`,{ns:'monitor'})}
                          required={required(key)}
                        >
                          <Input name={`config.${key}`} type={property.type} />
                        </Form.Item>
                      );
                    })}
                    <Divider />
                    <Flex h="end">
                      <Button
                        variant="primary"
                        ghost
                        className="mr-2"
                        onClick={close}
                      >
                        Cancel
                      </Button>
                      <SubmitButton
                        disabled={Object.keys(errors).length || !dirty}
                      >
                        {t('Submit')}
                      </SubmitButton>
                    </Flex>
                  </Form>
                </div>
              );
            }}
          </Formik>
        </div>
      )}
    </Modal>
  );
};

type MultiInputProps = ConfigJsonSchemaProperty & {
  propertyName: string;
  required?: boolean;
};
const MultiInput = ({
  propertyName,
  title,
  items,
  minItems,
  maxItems,
  required,
}: MultiInputProps) => {
  const {
    values: { config },
    setFieldValue,
  } = useFormikContext<EnrichedAssetMonitor>();

  const currentValue = config![propertyName] as (string | number)[];
  const Component = items?.type === 'string' ? Input : InputNumber;
  const { t } = useAssetTranslation();
  const baseName = `config.${propertyName}`;
  return (
    <Form.Item
      key={propertyName}
      name={baseName}
      label={t(`${title}`,{ns:'monitor'})}
      required={required}
      help={null}
    >
      <div>
        {currentValue.map((_, i) => (
          <Flex className="mb-2" key={`${baseName}.${i}`}>
            <span className="flex basis-full">
              <Component name={`${baseName}.${i}`} fast />
            </span>
            <span className={`flex basis-0`}>
              <Button
                className="border-0 pr-2 pt-0 bg-transparent focus:text-primary hover:text-primary shadow-none"
                colorOverrides={{
                  disabledBg: 'white',
                }}
                variant="primary"
                ghost
                disabled={currentValue.length <= (minItems ?? 0)}
                onClick={() =>
                  setFieldValue(
                    baseName,
                    currentValue.slice(0, i).concat(currentValue.slice(i + 1))
                  )
                }
              >
                <XCircle size={16} />
              </Button>
            </span>
          </Flex>
        ))}
      </div>
      <div className="text-danger text-sm absolute">
        <ErrorMessage
          name={baseName}
          render={error =>
            t(Array.isArray(error) ? error.filter(Boolean)[0] : error)
          }
        />
      </div>
      {currentValue.length < (maxItems ?? 0) && (
        <Flex h={'end'} className="mr-10">
          <Button
            variant="primary"
            disabled={!currentValue.some(Boolean)}
            onClick={() =>
              setFieldValue(baseName, [
                ...currentValue,
                items?.type === 'string' ? '' : 0,
              ])
            }
          >
            {t('Add')}
          </Button>
        </Flex>
      )}
    </Form.Item>
  );
};

export default MonitorConfigModal;
